import React from 'react';
import {
  type ConversationMode,
  type ConversationStepMode,
  type LandingPageFacade, // Import facade type
  useCreditLimitBanner,
} from '@ideoz/core';
import {
  showToast,
  IdeozLoading,
  Button,
  Footer,
  ConfirmProjectModal,
  WhatIsProjectDialog,
  ProjectsList,
  LoadingOverlay,
  AuthDialog,
  UpgradeRequestCard,
  Header,
  QuickActionChips,
  type QuickAction,
  UserLandsDialog,
  Dialog,
  DialogContent,
  DialogPortal,
  DialogOverlay,
  DialogTitle,
} from '../index';
import { useFileAttachments } from '../../hooks/useFileAttachments';
import { ChatTextAreaInternal as ChatTextArea } from '../molecules/ChatTextArea';
import { AppLayout } from '../layouts/AppLayout';
import { FolderMinus, FolderPlus } from 'lucide-react';
import OnboardingTemplate from './OnboardingTemplate';
import UpgradeCtaCard from '../molecules/UpgradeCtaCard';

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Landing Page Template Props
 *
 * DRAMATICALLY SIMPLIFIED with Facade Pattern!
 *
 * Before: 27+ individual props (state, actions, handlers...)
 * After: 1 facade + 4-5 platform-specific props
 *
 * The facade encapsulates all shared logic:
 * - State (isLoggedIn, isAppLoading, projects)
 * - Header configuration & user menu
 * - Navigation (toConversation, toProjectDetails, etc.)
 * - Conversation actions (create, reset, setId, setMode, setName)
 * - Project actions (create, delete, getList, setCurrent, etc.)
 */
export interface LandingPageTemplateProps {
  // ========== FACADE (all shared logic) ==========
  facade: LandingPageFacade;

  // ========== Platform-Specific Props ==========
  isWeb: boolean;

  // Web-specific
  authDialogState?: {
    isOpen: boolean;
    mode: 'login' | 'register';
    isLoading: boolean;
    openRegister: () => void;
    openLogin: () => void;
    closeDialog: () => void;
    switchMode: (mode: 'login' | 'register') => void;
    handleLogin: (email: string, password: string) => Promise<void>;
    handleRegister: (name: string, email: string, password: string) => Promise<void>;
    handleGoogleAuth: () => Promise<void>;
    handleNavigateToForgotPassword: () => void;
  };

  // Plugin-specific
  pluginLogin?: any;
  pluginStopInterval?: () => void;
  isAuthLoading?: boolean;
}

/**
 * Interface for openConversationAndDrawing user action (Plugin Environment)
 */
interface OpenConversationAndDrawingAction {
  type: 'openConversationAndDrawing';
  generateType: string;
  conversationID: string;
  conversationName: string;
  projectId?: string;
  timestamp?: string;
}

// ============================================================================
// LANDING PAGE TEMPLATE
// ============================================================================

const LandingPageTemplate: React.FC<LandingPageTemplateProps> = ({
  // FACADE - all shared logic organized
  facade,

  // Platform-specific
  isWeb,
  authDialogState,
  pluginLogin,
  pluginStopInterval,
  isAuthLoading = false,
}) => {
  // ========== Destructure Facade for Clean Usage ==========
  const {
    userLandsDialog,
    state: { isLoggedIn, isAppLoading, myProjects, isLoadingProjects, llmFamily, planDetails },
    header: {
      headerProps,
      userMenuOpen = false,
      onUserMenuClose,
      userMenuItems = [],
      userMenuSocialLinks = [],
      onRequestUpgrade,
      isRequestingUpgrade = false,
    },
    navigation: {
      toConversation: onNavigateToConversation,
      toProjectDetails: onNavigateToProjectDetails,
      toLanding: onNavigateToLanding,
    },
    conversation: {
      create: onCreateConversation,
      reset: onResetConversation,
      setId: onSetConversationId,
      setMode: onSetMode,
      setName: onSetName,
      setLlmFamily: onSetLlmFamily,
    },
    project: {
      projects: _, // Already have myProjects from state
      isLoading: __, // Already have isLoadingProjects from state
      create: onCreateProject,
      delete: onDeleteProject,
      getList: onGetUserProjects,
      setCurrent: onSetCurrentProjectId,
      setMetadata: onSetProjectMetaData,
      setDrawerVisibility: onSetShowProjectDetailsDrawer,
    },
  } = facade;

  // ========== Local State ==========
  const [isCreateProjectDialogOpen, setIsCreateProjectDialogOpen] = React.useState(false);
  const [isWhatIsProjectDialogOpen, setIsWhatIsProjectDialogOpen] = React.useState(false);
  const [isOnboardingDialogOpen, setIsOnboardingDialogOpen] = React.useState(false);
  const [waitingForAuth, setWaitingForAuth] = React.useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [project, setProject] = React.useState<any | null>(null);
  const [isCreatingProject, setIsCreatingProject] = React.useState(false);
  const [hasCheckedUserActions, setHasCheckedUserActions] = React.useState(false);
  const [isCheckingUserActions, setIsCheckingUserActions] = React.useState(!isWeb);
  const creditLimitBanner = useCreditLimitBanner();

  // ========== Computed Values ==========
  const userPoints = headerProps.points || 0;
  //const shouldShowUpgradeCard = userPoints <= 0;
  const shouldShowUpgradeCard = false; // Temporarily disable upgrade card

  // Check if user has base credits remaining (for conversation/analysis)
  const baseCredit = planDetails?.credits?.find((c: any) => c.type === 'base');
  const baseBalance =
    baseCredit != null
      ? typeof baseCredit.balance === 'string'
        ? parseInt(baseCredit.balance, 10)
        : (baseCredit.balance ?? 0)
      : 1; // If no base credit in planDetails, allow (don't block)

  // Hide ChatTextArea and disable chips if user is out of base credits
  const hasCredits = baseBalance > 0;

  // ========== useEffect: Check for openConversationAndDrawing user actions (Plugin) ==========
  React.useEffect(() => {
    if (isAppLoading) return;

    if (!isWeb && isLoggedIn && headerProps.user?.metadata?.userActions && !hasCheckedUserActions) {
      setHasCheckedUserActions(true);

      try {
        console.log('Checking user actions:', headerProps.user.metadata.userActions);

        const openConversationActions = headerProps.user.metadata.userActions.filter(
          (action: any): action is OpenConversationAndDrawingAction =>
            action.type === 'openConversationAndDrawing'
        );

        console.log('Found openConversationAndDrawing actions:', openConversationActions);

        const openConversationAction = openConversationActions.sort(
          (a: OpenConversationAndDrawingAction, b: OpenConversationAndDrawingAction) => {
            return new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime();
          }
        )[0];

        if (openConversationAction?.conversationID) {
          console.log(
            'Processing most recent openConversationAndDrawing action:',
            openConversationAction
          );

          onResetConversation();
          onSetConversationId(openConversationAction.conversationID);
          onSetMode('clarify');
          onSetName(openConversationAction.conversationName || 'New Conversation');

          if (openConversationAction.projectId) {
            console.log('Setting project ID:', openConversationAction.projectId);
            onSetCurrentProjectId(openConversationAction.projectId);
          }

          console.log('Navigating to conversation:', openConversationAction.conversationID);
          onNavigateToConversation(openConversationAction.conversationID);
        } else {
          console.log('No valid openConversationAndDrawing action found in user metadata');
          setIsCheckingUserActions(false);
        }
      } catch (error) {
        console.error('Error processing openConversationAndDrawing action:', error);
        setIsCheckingUserActions(false);
      }
    } else if (!isWeb && (!isLoggedIn || !headerProps.user?.metadata?.userActions)) {
      setIsCheckingUserActions(false);
      setHasCheckedUserActions(true);
    } else if (isWeb) {
      setIsCheckingUserActions(false);
    }
  }, [
    isAppLoading,
    isWeb,
    isLoggedIn,
    headerProps.user?.metadata,
    hasCheckedUserActions,
    onResetConversation,
    onSetConversationId,
    onSetMode,
    onSetName,
    onSetCurrentProjectId,
    onNavigateToConversation,
  ]);

  // ========== useEffect: Handle stored query parameter for auto-submit (Web) ==========
  React.useEffect(() => {
    if (isAppLoading) return;

    if (isWeb && typeof window !== 'undefined') {
      const storedQuery = localStorage.getItem('ideoz_pending_query');

      if (storedQuery && storedQuery.trim()) {
        handleSubmit(storedQuery.trim(), []);
        localStorage.removeItem('ideoz_pending_query');
      }
    }
    return undefined;
  }, [isAppLoading, isWeb]);

  // ========== useEffect: Monitor authentication state (Plugin) ==========
  React.useEffect(() => {
    if (isAppLoading) return;

    if (!isWeb && waitingForAuth && !isAuthLoading && isLoggedIn) {
      setWaitingForAuth(false);
    }
  }, [isAppLoading, isWeb, waitingForAuth, isAuthLoading, isLoggedIn]);

  // ========== useEffect: Fetch user projects ==========
  React.useEffect(() => {
    if (isAppLoading) return;

    if (isLoggedIn && headerProps.user?.id) {
      onGetUserProjects({ limit: 50, page: 1 })
        .then((result: any) => {
          console.log('Fetched user projects:', result);
        })
        .catch((error: any) => {
          console.error('Error fetching user projects:', error);
        });
    }
    // onGetUserProjects is stable (wrapped in useCallback), so we exclude it from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppLoading, isLoggedIn, headerProps.user?.id]);

  // ========== Event Handlers ==========

  /**
   * Handle chat text area submit
   */
  const handleSubmit = (text: string, attachmentIds: string[], llmFamily?: string) => {
    if (isAppLoading) return;
    if (!text.trim() && attachmentIds.length === 0) return;

    // Analytics: landing.message.sent tracked via Redux middleware

    onSetCurrentProjectId('');
    onResetConversation();
    onCreateConversation({
      init_message: text,
      mode: 'clarify',
      attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
      llmFamily: llmFamily,
    });

    onNavigateToConversation();
  };

  /**
   * Handle quick action chip click
   */
  const handleQuickActionClick = (action: QuickAction) => {
    if (isAppLoading) return;

    // Analytics: landing.quick_action.clicked tracked via data-track attribute

    onSetCurrentProjectId('');
    onResetConversation();
    onCreateConversation({
      init_message: action.prompt,
      mode: 'clarify',
      skip_analysis: true,
    });

    onNavigateToConversation();
  };

  /**
   * Handle "What is Ideoz Project?" click
   */
  const handleWhatIsIdeozProject = () => {
    setIsWhatIsProjectDialogOpen(true);
    // Analytics: landing.what_is_project.opened tracked via data-track attribute
  };

  /**
   * Handle close "What is Project" dialog
   */
  const handleCloseWhatIsProjectDialog = () => {
    setIsWhatIsProjectDialogOpen(false);
  };

  /**
   * Handle create project from "What is Project" dialog
   */
  const handleCreateProjectFromDialog = () => {
    setIsWhatIsProjectDialogOpen(false);
    setIsCreateProjectDialogOpen(true);
  };

  /**
   * Handle create account from "What is Project" dialog
   */
  const handleCreateAccountFromDialog = () => {
    setIsWhatIsProjectDialogOpen(false);
    if (isWeb && authDialogState) {
      authDialogState.openRegister();
    } else if (pluginLogin) {
      setWaitingForAuth(true);
      pluginLogin('register');
    }
  };

  /**
   * Handle create project
   */
  const handleCreateProject = async (projectName: string) => {
    setIsCreatingProject(true);
    try {
      const projectData = { name: projectName };
      const result = await onCreateProject(projectData);

      if (result.type === 'project/create/fulfilled') {
        console.log('Project created successfully:', result.payload);
        const projectId = result.payload?.project_id || result.payload?.data?.project_id;

        if (projectId) {
          setIsCreateProjectDialogOpen(false);

          onSetCurrentProjectId(projectId);
          onSetProjectMetaData(null);
          onNavigateToProjectDetails(projectId.toString());
          showToast({
            message: `Your project created successfully. Add more context to get sharper insights.`,
            severity: 'success',
            type: 'success',
          });
          facade.project.setDrawerVisibility(true); // open drawer immediately for first project creation
        } else {
          console.error('No project ID returned from API');
        }
      } else {
        console.error('Failed to create project:', result.error);
      }
    } catch (error) {
      console.error('Error creating project:', error);
    } finally {
      setIsCreatingProject(false);
    }
  };

  /**
   * Handle close delete dialog
   */
  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    // Analytics: project.delete.cancelled tracked via new system
  };

  /**
   * Handle open onboarding dialog
   */
  const handleOpenOnboardingDialog = () => {
    setIsOnboardingDialogOpen(true);
  };

  /**
   * Handle close onboarding dialog
   */
  const handleCloseOnboardingDialog = () => {
    setIsOnboardingDialogOpen(false);
  };

  /**
   * Handle delete project action
   */
  const handleDeleteProjectAction = async (projectName: string) => {
    if (projectName.trim() !== project?.name) {
      console.error('Project name does not match');
      return;
    }
    if (!project) {
      console.error('Project not found');
      return;
    }

    try {
      const result = await onDeleteProject(project.id);

      if (result.type === 'project/delete/fulfilled') {
        showToast({
          message: `Project "${project.name}" deleted successfully`,
          severity: 'success',
          type: 'success',
        });
        // Analytics: project.deleted tracked via Redux middleware
        setIsDeleteDialogOpen(false);
      } else {
        console.error('Failed to delete project:', result.error);
      }
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  /**
   * Handle open create project dialog
   */
  const handleOpenCreateProject = () => {
    if (isLoggedIn) {
      setIsCreateProjectDialogOpen(true);
    } else {
      if (isWeb && authDialogState) {
        const pendingData = {
          action: 'create_project',
          timestamp: Date.now(),
        };
        localStorage.setItem('ideoz_pending_action', JSON.stringify(pendingData));
        authDialogState.openRegister();
      } else if (pluginLogin) {
        setWaitingForAuth(true);
        pluginLogin('register');
      }
    }
  };

  /**
   * Handle close project modal
   */
  const handleCloseProjectModal = () => {
    setIsCreateProjectDialogOpen(false);
  };

  /**
   * Handle project click
   */
  const handleProjectClick = (projectId: string) => {
    onSetCurrentProjectId(projectId);
    onSetProjectMetaData(null);
    onNavigateToProjectDetails(projectId);
  };

  /**
   * Handle new project click
   */
  const handleNewProjectClick = () => {
    setIsCreateProjectDialogOpen(true);
  };

  /**
   * Handle project delete click
   */
  const handleProjectDeleteClick = (id: string, name: string) => {
    setProject({ id, name });
    setIsDeleteDialogOpen(true);
    // Analytics: project.delete.clicked tracked via data-track attribute
  };

  // ========== Render ==========
  return (
    <>
      {/* Loading overlay for authentication */}
      <LoadingOverlay isLoading={isAuthLoading} onCancel={pluginStopInterval || (() => {})} />

      {/* Loading state */}
      {isAppLoading || isCheckingUserActions ? (
        <div className="h-[100vh] flex items-center justify-center flex-col">
          <IdeozLoading size="lg" />
        </div>
      ) : (
        <AppLayout
          currentPath="/"
          sidebar={facade.sidebar}
          user={{
            isLoggedin: isLoggedIn,
            name: headerProps.user?.name,
            avatar: headerProps.user?.avatar,
          }}
          userMenuProps={
            isLoggedIn
              ? {
                  points: headerProps.points,
                  pointsLabel: 'Ideoz AI Points',
                  menuItems: userMenuItems,
                  socialLinks: userMenuSocialLinks,
                  onRequestUpgrade,
                  isRequestingUpgrade,
                  canRequestUpgrade: (headerProps.points || 0) <= 0,
                  isWeb,
                }
              : undefined
          }
          isWeb={isWeb}
          testId="landing-sidemenu"
        >
          {/* Header - Show full header when not logged in, or just banner when logged in */}

          <Header
            hideHeaderContent={isLoggedIn}
            {...headerProps}
            className={isWeb ? 'sticky top-0 w-full' : ''}
            isWeb={isWeb}
            creditLimitBanner={
              creditLimitBanner.isVisible
                ? {
                    ...creditLimitBanner,
                    onAction: () => facade.navigation.toBilling(),
                    onDismiss: () => creditLimitBanner.setVisible(false),
                  }
                : undefined
            }
          />

          {/* Show upgrade request if user has no points */}
          {shouldShowUpgradeCard && !isAppLoading ? (
            <>
              <div
                className={`flex flex-col items-center justify-center ${
                  isWeb ? 'min-h-[calc(100vh-theme(spacing.header))]' : 'min-h-[calc(100vh-100px)]'
                }`}
              >
                <UpgradeRequestCard
                  user={isLoggedIn ? headerProps.user : null}
                  onOpenRegister={
                    isWeb && authDialogState
                      ? authDialogState.openRegister
                      : () => pluginLogin?.('register')
                  }
                  onOpenLogin={
                    isWeb && authDialogState
                      ? authDialogState.openLogin
                      : () => pluginLogin?.('login')
                  }
                  variant={isWeb ? 'web' : 'plugin'}
                />
              </div>
              {!isWeb && <Footer />}
            </>
          ) : (
            <>
              <div
                className={`max-w-landing-page mx-auto min-h-[calc(100vh-theme(spacing.header))] py-10 ${isWeb ? 'flex flex-col justify-center' : ''}`}
              >
                <div className={`${isWeb ? '' : 'my-16'}`}>
                  {/* Heading */}
                  <div className="flex flex-col gap-4">
                    <h2
                      className={`font-medium lg:font-semibold text-primary-800 text-center leading-tight text-3xl lg:text-[32px]`}
                    >
                      Uncover problems. Prototype solutions. All in one flow
                    </h2>
                    <p className={`text-center leading-tight text-base text-primary-600`}>
                      Let Ideoz AI help you discover, prototype, and share—smarter and faster than
                      ever.
                    </p>
                  </div>

                  {/* Chat Text Area or Upgrade CTA (out of credits) */}
                  <div className={`flex justify-center ${isWeb ? 'mt-9 mb-6' : 'mt-8 mb-8'}`}>
                    {hasCredits ? (
                      <ChatTextArea
                        onSubmit={handleSubmit}
                        placeholder="Type anything to Nexus …"
                        data-testid="landing-chat-textarea"
                        useFileAttachmentsHook={useFileAttachments}
                        isWeb={isWeb}
                        initialLlmFamily={llmFamily as any}
                        onLlmFamilyChange={(newModel) => {
                          // Update Redux state when model changes
                          onSetLlmFamily(newModel);
                        }}
                      />
                    ) : (
                      <div className="w-full flex flex-col items-center justify-center max-w-[830px]">
                        <UpgradeCtaCard
                          className="w-11/12 rounded-lg"
                          title="You've used all your credits"
                          description="Upgrade your plan or purchase additional credits to continue."
                          buttonText="Upgrade"
                          onUpgrade={facade.navigation.toBilling}
                          data-testid="upgrade-cta-landing"
                        />
                      </div>
                    )}
                  </div>

                  {/* Quick Action Chips */}
                  <div className="mb-20 mx-auto" data-testid="quick-action-chips-section">
                    <QuickActionChips
                      onActionClick={handleQuickActionClick}
                      isWeb={isWeb}
                      testId="landing-quick-actions"
                      disabled={!hasCredits}
                    />
                  </div>
                  {myProjects.length > 0 || isLoadingProjects ? (
                    <>
                      <h2
                        className="text-lg text-start font-medium lg:font-semibold text-gray-800 mb-4"
                        data-testid="projects-list"
                      >
                        My projects
                      </h2>
                      <ProjectsList
                        projects={myProjects}
                        isLoading={isLoadingProjects}
                        onProjectClick={handleProjectClick}
                        onProjectDelete={handleProjectDeleteClick}
                        onNewProjectClick={handleNewProjectClick}
                        showRecent={false}
                        planDetails={planDetails}
                        upgradeLink={facade.navigation.toBilling}
                        isWeb={isWeb}
                      />
                    </>
                  ) : (
                    <div
                      className={`flex flex-col justify-center items-center ${isWeb ? 'gap-5' : 'gap-4'}`}
                    >
                      <p
                        className={`text-gray-600 text-center font-regular leading-[120%] ${
                          isWeb ? 'text-lg' : 'text-[11px]'
                        }`}
                      >
                        {isLoggedIn
                          ? ' Looking for deeper insights? Create a free project'
                          : 'Looking for deeper insights? create a free account now'}
                      </p>
                      <div className="flex justify-center gap-3">
                        {isLoggedIn ? (
                          <>
                            <Button
                              variant="primary"
                              size={isWeb ? 'xl' : 'sm'}
                              onClick={handleOpenCreateProject}
                              data-testid="create-project-button"
                              data-track="landing_create_project"
                              style={{ backgroundColor: '#1E2340', color: '#fff' }}
                            >
                              Create new project
                            </Button>
                            <Button
                              variant="secondary-gray"
                              size={isWeb ? 'xl' : 'sm'}
                              onClick={() => setIsWhatIsProjectDialogOpen(true)}
                              className=""
                              data-testid="what-is-project-button"
                              data-track="landing_what_is_project"
                            >
                              What is Ideoz project?
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="primary"
                            size={isWeb ? 'xl' : 'sm'}
                            onClick={
                              isWeb
                                ? authDialogState?.openRegister
                                : () => pluginLogin?.('register')
                            }
                            className=""
                            data-testid="create-free-account-button"
                          >
                            Create free account
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {!isWeb && <Footer />}
            </>
          )}
        </AppLayout>
      )}

      {/* ========== Modals ========== */}

      {/* Create Project Modal */}
      <ConfirmProjectModal
        isOpen={isCreateProjectDialogOpen}
        onClose={handleCloseProjectModal}
        onCreate={handleCreateProject}
        title="Create new project"
        description="Give your project a clear, focused name. e.g., 'Checkout drop-off issue'"
        info={{
          title: 'What is project?',
          desc: 'Projects help you organize your thoughts, stay in flow, and come back anytime without starting over.',
        }}
        buttonText="Create"
        buttonType="primary"
        iconName={FolderPlus}
        isLoading={isCreatingProject}
        classNameDescription="-mt-[10px]"
        hasCreatedProject={headerProps.user?.metadata?.hasCreatedProject}
        isWeb={isWeb}
      />

      {/* Delete Project Modal */}
      <ConfirmProjectModal
        isOpen={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onDelete={() => {
          setIsDeleteDialogOpen(false);
        }}
        onCreate={handleDeleteProjectAction}
        title="Delete project"
        info={{
          title: 'What does it mean deleting a project?',
          desc: 'We encourage you to keep your projects as deleting it will let you miss all the data and not be able to retrieve it back',
        }}
        buttonText="Delete"
        buttonType="danger"
        iconName={FolderMinus}
        inputLabel="Please write the project name to delete it"
        expectedProjectName={project?.name}
        targetAction="delete"
        showProjectHint={false}
        hasCreatedProject={headerProps.user?.metadata?.hasCreatedProject}
        isWeb={isWeb}
      />

      {/* What is Project Dialog */}
      <WhatIsProjectDialog
        isOpen={isWhatIsProjectDialogOpen}
        onClose={handleCloseWhatIsProjectDialog}
        isLoggedIn={isLoggedIn}
        onCreateProject={handleCreateProjectFromDialog}
        onCreateAccount={handleCreateAccountFromDialog}
        isWeb={isWeb}
      />

      {/* User Lands Dialog - Shows when user is new (isNew === true from visitors/check API) */}
      <UserLandsDialog
        isOpen={userLandsDialog.isOpen}
        onClose={userLandsDialog.onClose}
        isWeb={isWeb}
      />

      {/* Onboarding Dialog */}
      <Dialog open={isOnboardingDialogOpen} onOpenChange={handleCloseOnboardingDialog}>
        <DialogPortal>
          <DialogOverlay />
          <DialogContent className="max-w-full w-full h-[100vh] p-0 overflow-hidden border-0">
            <DialogTitle className="sr-only">Onboarding</DialogTitle>
            <OnboardingTemplate onComplete={handleCloseOnboardingDialog} facade={facade} />
          </DialogContent>
        </DialogPortal>
      </Dialog>

      {/* Auth Dialog for web environment */}
      {isWeb && authDialogState && (
        <AuthDialog
          isOpen={authDialogState.isOpen}
          onClose={authDialogState.closeDialog}
          mode={authDialogState.mode}
          onModeChange={authDialogState.switchMode}
          onLogin={authDialogState.handleLogin}
          onRegister={authDialogState.handleRegister}
          onGoogleAuth={authDialogState.handleGoogleAuth}
          isLoading={authDialogState.isLoading}
          isWeb={isWeb}
          onNavigateToForgotPassword={authDialogState.handleNavigateToForgotPassword}
        />
      )}
    </>
  );
};

export default LandingPageTemplate;
